import { useEffect, useRef, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { ChartLoadingSkeleton } from '../layout/ChartLoadingSkeleton';
import { fetchCandles } from '../lib/api/candles';
import type { Candle, PriceAlarm } from '../types/api';
import { type AlarmTimeframe, getAlarmCandleQuery } from './alarmTimeframes';

interface AlarmPriceChartProps {
  symbol: string;
  pricescale: number;
  timeframe: AlarmTimeframe;
  targetPrice: number | null;
  currentPrice: number | null;
  alarms: PriceAlarm[];
  deletingAlarmId: string | null;
  onTargetPriceChange(price: number): void;
  onEditPrice(): void;
  onDeleteAlarm(id: string): void;
}

interface ChartSize {
  width: number;
  height: number;
}

interface ChartDomain {
  minPrice: number;
  maxPrice: number;
}

const chartPadding = { top: 18, right: 58, bottom: 28, left: 12 };
const handleWidth = 86;
const handleHeight = 24;

export function AlarmPriceChart({ symbol, pricescale, timeframe, targetPrice, currentPrice, alarms, deletingAlarmId, onTargetPriceChange, onEditPrice, onDeleteAlarm }: AlarmPriceChartProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState<ChartSize>({ width: 0, height: 0 });
  const [candles, setCandles] = useState<Candle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domain, setDomain] = useState<ChartDomain | null>(null);
  const activeAlarmPrices = alarms.filter(isRenderableAlarm);
  const alarmPriceValues = activeAlarmPrices.map((alarm) => alarm.target_price);
  const alarmPriceSignature = alarmPriceValues.join('|');
  const model = buildChartModel(candles, domain, size);
  const targetY = targetPrice !== null && model ? model.priceToY(targetPrice) : null;
  const currentY = currentPrice !== null && model ? model.priceToY(currentPrice) : null;
  const formatPrice = (price: number) => formatScaledPrice(price, pricescale);
  const dragRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragMovedRef = useRef(false);

  useEffect(() => {
    if (!container) return undefined;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [container]);

  useEffect(() => {
    let cancelled = false;
    const query = getAlarmCandleQuery(timeframe);
    setLoading(true);
    setError('');
    void fetchCandles({ symbol, ...query }).then((nextCandles) => {
      if (cancelled) return;
      const filteredCandles = nextCandles.filter(isRenderableCandle).sort((a, b) => a.time - b.time);
      setCandles(filteredCandles);
      setDomain(filteredCandles.length > 0 ? buildInitialDomain(filteredCandles, currentPrice, targetPrice, activeAlarmPrices) : null);
    }).catch((err) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : 'Failed to load candles');
      setCandles([]);
      setDomain(null);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [symbol, timeframe]);

  useEffect(() => {
    if (candles.length === 0) return;
    setDomain((currentDomain) => {
      if (currentDomain === null) {
        return buildInitialDomain(candles, currentPrice, targetPrice, activeAlarmPrices);
      }
      if (dragRef.current) return currentDomain;
      return expandDomain(currentDomain, [currentPrice, targetPrice, ...alarmPriceValues]);
    });
  }, [alarmPriceSignature, candles, currentPrice, targetPrice]);

  function updatePriceFromPointer(clientY: number) {
    if (!model) return;
    const bounds = container?.getBoundingClientRect();
    if (!bounds) return;
    onTargetPriceChange(roundToPricescale(model.yToPrice(clientY - bounds.top), pricescale));
  }

  return (
    <div ref={setContainer} className="alarm-price-chart" data-vaul-no-drag="" onPointerDown={(event) => event.stopPropagation()}>
      {model && (
        <svg
          className="alarm-price-svg"
          viewBox={`0 0 ${size.width} ${size.height}`}
          role="img"
          aria-label={`${symbol} alarm price chart`}
        >
          {model.gridLines.map((line) => (
            <g key={line.price}>
              <line x1={0} x2={size.width} y1={line.y} y2={line.y} className="alarm-chart-grid" />
              <text x={size.width - 8} y={line.y - 4} className="alarm-chart-axis-label">{formatPrice(line.price)}</text>
            </g>
          ))}
          {model.candles.map((candle) => (
            <g key={candle.time} className={candle.close >= candle.open ? 'up' : 'down'}>
              <line x1={candle.x} x2={candle.x} y1={candle.highY} y2={candle.lowY} className="alarm-candle-wick" />
              <rect x={candle.x - candle.width / 2} y={candle.bodyY} width={candle.width} height={candle.bodyHeight} rx={1.5} className="alarm-candle-body" />
            </g>
          ))}
          {activeAlarmPrices.map((alarm) => {
            const y = model.priceToY(alarm.target_price);
            const deleting = deletingAlarmId === alarm.id;
            const controlWidth = deleting ? 82 : 62;
            return (
              <g key={alarm.id}>
                <line x1={chartPadding.left} x2={size.width - chartPadding.right + 12} y1={y} y2={y} className="alarm-existing-line" />
                <foreignObject x={Math.max(chartPadding.left, size.width - chartPadding.right - controlWidth)} y={y - 12} width={controlWidth + 4} height={24}>
                  <button
                    type="button"
                    className={deleting ? 'alarm-existing-control deleting' : 'alarm-existing-control'}
                    data-vaul-no-drag=""
                    disabled={deleting}
                    aria-label={`Delete alarm at ${formatPrice(alarm.target_price)}`}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteAlarm(alarm.id);
                    }}
                  >
                    <span>{deleting ? 'Deleting...' : formatPrice(alarm.target_price)}</span>
                    {!deleting && <X size={8} strokeWidth={2.5} aria-hidden="true" />}
                  </button>
                </foreignObject>
              </g>
            );
          })}
          {currentPrice !== null && currentY !== null && (
            <g>
              <line x1={chartPadding.left} x2={size.width - chartPadding.right + 12} y1={currentY} y2={currentY} className="alarm-current-line" />
              <foreignObject x={Math.max(chartPadding.left, size.width - chartPadding.right - 66)} y={currentY - 24} width={70} height={20}>
                <div className="alarm-current-pill">{formatPrice(currentPrice)}</div>
              </foreignObject>
            </g>
          )}
          {targetPrice !== null && targetY !== null && (
            <g>
              <line x1={chartPadding.left} x2={size.width - chartPadding.right + 12} y1={targetY} y2={targetY} className="alarm-target-line" />
              <foreignObject x={Math.max(chartPadding.left, size.width * 0.5 - handleWidth / 2)} y={targetY - handleHeight / 2} width={handleWidth} height={handleHeight}>
                <button
                  type="button"
                  className="alarm-target-handle"
                  data-vaul-no-drag=""
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    dragRef.current = true;
                    dragStartYRef.current = event.clientY;
                    dragMovedRef.current = false;
                    event.currentTarget.setPointerCapture(event.pointerId);
                  }}
                  onPointerMove={(event) => {
                    if (!dragRef.current) return;
                    event.preventDefault();
                    event.stopPropagation();
                    if (Math.abs(event.clientY - dragStartYRef.current) > 2) dragMovedRef.current = true;
                    updatePriceFromPointer(event.clientY);
                  }}
                  onPointerUp={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    dragRef.current = false;
                    event.currentTarget.releasePointerCapture(event.pointerId);
                  }}
                  onPointerCancel={(event) => {
                    event.stopPropagation();
                    dragRef.current = false;
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (dragMovedRef.current) return;
                    onEditPrice();
                  }}
                >
                  <span>{formatPrice(targetPrice)}</span>
                  <Pencil size={11} strokeWidth={2.5} aria-hidden="true" />
                </button>
              </foreignObject>
            </g>
          )}
        </svg>
      )}
      {loading && candles.length === 0 && <ChartLoadingSkeleton className="alarm-chart-skeleton" />}
      {loading && candles.length > 0 && <div className="alarm-chart-loading-shade" aria-label="Loading candles" />}
      {!loading && error && <div className="alarm-chart-state error">{error}</div>}
      {!loading && !error && candles.length === 0 && <div className="alarm-chart-state">No candles</div>}
    </div>
  );
}

function buildChartModel(candles: Candle[], domain: ChartDomain | null, size: ChartSize) {
  if (candles.length === 0 || size.width <= 0 || size.height <= 0 || domain === null) return null;
  const chartWidth = Math.max(1, size.width - chartPadding.left - chartPadding.right);
  const chartHeight = Math.max(1, size.height - chartPadding.top - chartPadding.bottom);
  const priceRange = Math.max(domain.maxPrice - domain.minPrice, Math.max(domain.maxPrice, 1) * 0.01);
  const step = candles.length > 1 ? chartWidth / (candles.length - 1) : chartWidth;
  const width = Math.max(3, Math.min(12, step * 0.32));

  const priceToY = (price: number) => chartPadding.top + ((domain.maxPrice - price) / priceRange) * chartHeight;
  const yToPrice = (y: number) => {
    const clampedY = Math.min(chartPadding.top + chartHeight, Math.max(chartPadding.top, y));
    return domain.maxPrice - ((clampedY - chartPadding.top) / chartHeight) * priceRange;
  };

  return {
    priceToY,
    yToPrice,
    gridLines: Array.from({ length: 4 }, (_, index) => {
      const price = domain.minPrice + (priceRange * index) / 3;
      return { price, y: priceToY(price) };
    }),
    candles: candles.map((candle, index) => {
      const x = chartPadding.left + (candles.length === 1 ? chartWidth / 2 : step * index);
      const openY = priceToY(candle.open);
      const closeY = priceToY(candle.close);
      return {
        ...candle,
        x,
        width,
        highY: priceToY(candle.high),
        lowY: priceToY(candle.low),
        bodyY: Math.min(openY, closeY),
        bodyHeight: Math.max(2, Math.abs(openY - closeY)),
      };
    }),
  };
}

function buildInitialDomain(candles: Candle[], currentPrice: number | null, targetPrice: number | null, alarms: PriceAlarm[]): ChartDomain {
  const prices = candles.flatMap((candle) => [candle.high, candle.low]);
  if (targetPrice !== null) prices.push(targetPrice);
  if (currentPrice !== null) prices.push(currentPrice);
  prices.push(...alarms.map((alarm) => alarm.target_price));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const spread = Math.max(maxPrice - minPrice, Math.max(maxPrice, 1) * 0.01);
  return {
    minPrice: minPrice - spread * 0.12,
    maxPrice: maxPrice + spread * 0.12,
  };
}

function expandDomain(domain: ChartDomain, prices: Array<number | null>): ChartDomain {
  const values = prices.filter((price): price is number => price !== null && Number.isFinite(price) && price > 0);
  if (values.length === 0) return domain;
  const minPrice = Math.min(...values);
  const maxPrice = Math.max(...values);
  if (minPrice >= domain.minPrice && maxPrice <= domain.maxPrice) return domain;
  const spread = Math.max(domain.maxPrice - domain.minPrice, Math.max(maxPrice, 1) * 0.01);
  return {
    minPrice: Math.min(domain.minPrice, minPrice - spread * 0.08),
    maxPrice: Math.max(domain.maxPrice, maxPrice + spread * 0.08),
  };
}

function isRenderableCandle(candle: Candle): boolean {
  return [candle.open, candle.high, candle.low, candle.close].every((value) => Number.isFinite(value) && value > 0);
}

function isRenderableAlarm(alarm: PriceAlarm): boolean {
  return Number.isFinite(alarm.target_price) && alarm.target_price > 0;
}

function formatScaledPrice(price: number, pricescale: number): string {
  return price.toFixed(priceDecimals(pricescale));
}

function roundToPricescale(price: number, pricescale: number): number {
  const decimals = priceDecimals(pricescale);
  const factor = 10 ** decimals;
  return Math.round(price * factor) / factor;
}

function priceDecimals(pricescale: number): number {
  if (!Number.isFinite(pricescale) || pricescale <= 1) return 2;
  return Math.min(8, Math.max(0, Math.round(Math.log10(pricescale))));
}
